import classNames from 'classnames';
import * as React from 'react';
import { useMemo, useRef } from 'react';
import { CloseIcon, Edit1Icon, PlayIcon } from 'tdesign-icons-react';
import { Button } from 'tdesign-react';

import { LIMIT_AUDIO } from '@/const/form';
import { UserResourceType } from '@/interface/library';
import { EAsyncStatus, RichMediaValue } from '@/interface/media';
import { INewUploadIProps } from '@/interface/upload';
import { ILibraryDialogRef } from '@/pages/library/dialog';

import { emitRichMediaFile, getRichLocalPath } from '../change-data';
import { UploadContent } from '../core/common';
import UploadAIContent from '../core/common/content/ai';
import LibraryContent from '../core/common/content/library';
import UploadDialog from '../core/dialog';
import styles from './audio.module.less';

/**
 * 上传音频组件
 */
export const UploadAudio: React.FC<INewUploadIProps> = (props) => {
  const {
    limit = { ...LIMIT_AUDIO, fileType: LIMIT_AUDIO.type },
    onChange,
    asyncUploadOnChange,
    value,
    style,
    disabled,
    hasSetting = false,
    showOption = [true, true, true],
    itemStyle = [{}, {}, {}],
    lineStyle = {},
  } = props;

  const dialogRef = useRef<ILibraryDialogRef>(null);

  const loading = React.useMemo(() => {
    if (!asyncUploadOnChange) {
      return false;
    }
    if (value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING) {
      return true;
    }
    return false;
  }, [value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING]);


  const handleUploadOnchange = (params: { url: string; name: string }) => {
    const { url, name } = params;
    emitRichMediaFile(url);
    const localPath = getRichLocalPath(url);
    const result = {
      ...value,
      volume: 100,
      url: {
        ...value?.url,
        url,
        localPath,
        name: name || localPath,
      },
    };
    handlePropsOnchange(result); // 区分场景
  };

  const handleDialogOnchange = (result: RichMediaValue) => {
    handlePropsOnchange(result);
  };

  const handlePropsOnchange = (result: RichMediaValue) => {
    onChange?.({ ...result, promptText: value?.promptText });
  };

  const handleDelete = () => {
    if (disabled) {
      return;
    }
    handleUploadOnchange({ url: '', name: '' });
  };

  const handleOpenDialog = () => {
    // if (disabled) {
    //   return;
    // }
    dialogRef.current?.open();
  };

  const getHeader = () => (
    <div className={styles.modalTitle}>
      <span style={{ flexGrow: 1 }}>音频编辑</span>
    </div>
  );

  const len = useMemo(() => showOption?.filter(((e: boolean) => e))?.length, [showOption]);

  return (
    <div
      className={classNames(styles.uploadWrap, {
        ['nova-disabled']: disabled,
      })}
      style={{ ...style }}
    >
      {!value?.url?.url ? (
        <div className={styles.line} style={lineStyle}>
          {
            showOption?.[0]
              ? <div className={classNames(
                styles[`item_${len}`],
                { [styles.oneItemLoading]: loading },
              )} style={itemStyle?.[0]}>
                <UploadContent
                  uploadAreaOnChange={handleUploadOnchange}
                  limit={limit}
                  // 上传中的状态需要这个处理，AI，素材库都是直接同步操作，无需此函数
                  asyncUploadOnChange={asyncUploadOnChange}
                  {...props}
                  text={'上传音频'}
                />
              </div> : null
          }
          {
            loading ? null : <>
              {
                showOption?.[1]
                  ? <div className={classNames(styles[`item_${len}`])} style={itemStyle?.[1]}>
                    <UploadAIContent
                      {...props}
                      type={UserResourceType.RESOURCE_TYPE_AUDIO}
                    />
                  </div> : null
              }
              {
                showOption?.[2]
                  ? <div className={classNames(styles[`item_${len}`])} style={itemStyle?.[2]}>
                    <LibraryContent
                      {...props}
                      type={UserResourceType.RESOURCE_TYPE_AUDIO}
                    />
                  </div> : null
              }
            </>
          }
        </div>
      ) : (
        <div className={styles.showWrap} style={style}>
          <div className={styles.leftItem} onClick={handleOpenDialog}>
            <div className={styles.playIcon}>
              <PlayIcon size={16} />
            </div>
              <div className={styles.name}>{value?.url?.name}</div>
          </div>
          <div className={styles.rightItem}>
            <Button
              icon={<Edit1Icon size={16} />}
              className={styles.opBtnSmall}
              variant="text"
              onClick={handleOpenDialog}
              disabled={disabled}
              style={itemStyle?.[0]}
            />
            <Button
              icon={<CloseIcon size={16} />}
              className={styles.opBtnSmall}
              variant="text"
              disabled={disabled}
              onClick={handleDelete}
              style={itemStyle?.[0]}
            />
          </div>
        </div>
      )}
      <div className={styles.uploadArea}>
        <UploadDialog
          {...props}
          dialogRef={dialogRef}
          type={UserResourceType.RESOURCE_TYPE_AUDIO}
          dialogConfig={{
            header: getHeader(),
          }}
          handleDialogOnchange={handleDialogOnchange}
          hasSetting={hasSetting}
        />
      </div>
    </div>
  );
};

export default UploadAudio;
